/**
 * ScheduleDraftFlightDetailPanel — ScheduleDraft 侧栏航班详情与编辑面板。
 *
 * 提交时构建 UPDATE_FLIGHT_FIELDS EditCommand 交给 editPipeline.executeCommand，
 * 由 Pipeline 执行 validate → recalculate → apply 到 localState。
  * 删除操作进入本地编辑 Pipeline，保存前可通过 Undo 撤回。
 *
 * 设计文档 → docs/20_workspace_ux/21_workspace_layout.md
 * 骨架关联 → components/ScheduleDraftFlightForm.jsx (表单体),
 *             hooks/useScheduleDraftEditPipeline.js (Pipeline),
 *             components/editor/ScheduleDraftWorkspaceSidebar.jsx (装配层)
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Descriptions, Empty, Form, Modal, Space, Tag, Typography, message } from "antd";
import FlightTimeDisplay from "../../../../components/DateTimeDisplay/FlightTimeDisplay";
import useFlightEditRouteReferenceData from "../hooks/useFlightEditRouteReferenceData";
import { UPDATE_FLIGHT_FIELDS } from "../lib/editCommand";
import { getFlightRouteBlockTime, getFlightRouteName } from "../lib/flightDataUtils";
import { getFlightTimeDisplayInfo } from "../lib/flightTimingUtils";
import ScheduleDraftFlightForm from "./ScheduleDraftFlightForm";
import { buildFlightFormValues } from "./editor/timetable/timetableRowModel";

const { Text } = Typography;

function formatBlockTime(minutes) {
    if (!minutes) {
        return "--";
    }

    return `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, "0")}`;
}

function renderTimeValue(value, sourceLabel, showSource, airportCode, baseUtcTime = null) {
    return (
        <Space size={4} wrap>
            <FlightTimeDisplay utcTime={value} airportCode={airportCode} baseUtcTime={baseUtcTime} stdTime={baseUtcTime} showDate />
            {showSource ? <Tag>{sourceLabel}</Tag> : null}
        </Space>
    );
}

const ScheduleDraftFlightDetailPanel = ({
    selectedItem,
    draftId,
    executeCommand,
    onDeleteFlight,
    deletingFlight,
    canUpdateFlight,
    canDeleteFlight,
}) => {
    const flight = selectedItem?.meta?.flight;
    const ground = selectedItem?.itemType === "ground" || selectedItem?.meta?.itemType === "ground" ? selectedItem?.meta?.ground : null;
    const isEditableTrack = selectedItem?.meta?.trackKind === "editable";
    const isDeletedFlight = String(flight?.change_status || "").toUpperCase() === "DELETED";
    const usesEffectiveTime = Boolean(selectedItem?.meta?.usesEffectiveTime);
    const timeInfo = getFlightTimeDisplayInfo(flight, { useEffectiveTime: usesEffectiveTime });
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const routeReferenceDataRef = useRef({});
    const loadRouteReferenceDataForEdit = useFlightEditRouteReferenceData();

    const handleReferenceDataChange = useCallback((referenceData) => {
        routeReferenceDataRef.current = referenceData || {};
    }, []);

    useEffect(() => {
        if (!flight) {
            routeReferenceDataRef.current = {};
            form.resetFields();
            return;
        }

        form.resetFields();
        form.setFieldsValue(buildFlightFormValues(flight));
    }, [flight, form]);

    const handleSubmit = async () => {
        try {
            if (!flight || !executeCommand || isDeletedFlight) {
                return;
            }

            const values = await form.validateFields();
            setSubmitting(true);
            const referenceData = await loadRouteReferenceDataForEdit({
                values,
                currentFlight: flight,
                referenceData: routeReferenceDataRef.current,
            });

            await executeCommand({
                type: UPDATE_FLIGHT_FIELDS,
                draftId,
                flightId: flight.id,
                payload: values,
                meta: {
                    source: "sidebar",
                    referenceData,
                },
            });
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            if (error?.code === "ROUTE_REFERENCE_DATA_LOAD_FAILED") {
                message.error(error.message ? `航路数据加载失败，修改未应用：${error.message}` : "航路数据加载失败，修改未应用");
                return;
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = () => {
        if (!flight) {
            return;
        }

        Modal.confirm({
            title: `确认删除航班 ${flight.flight_number || "未命名航班"}`,
            content: "删除会先标记到本地编辑，保存前可通过撤销恢复。",
            okText: "确认删除",
            cancelText: "取消",
            okButtonProps: { danger: true, loading: deletingFlight },
            onOk: () => onDeleteFlight?.(flight.id),
        });
    };

    return (
        <Card title="当前选中" className="schedule-draft-side-card" size="small">
            {flight ? (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    <Space wrap size={[6, 6]}>
                        <Tag color="blue">{selectedItem?.meta?.trackKind || "editable"}</Tag>
                        <Tag>{selectedItem?.meta?.sourceLabel || "当前计划"}</Tag>
                        <Tag>{flight.change_status || "UNCHANGED"}</Tag>
                        {flight._dirty && <Tag color="warning">已修改</Tag>}
                    </Space>

                    <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label="航班号">{flight.flight_number || "--"}</Descriptions.Item>
                        <Descriptions.Item label="飞机">{flight.aircraft_reg || "未分配"}</Descriptions.Item>
                        <Descriptions.Item label="航线">{`${flight.departure_airport || "--"} -> ${flight.arrival_airport || "--"}`}</Descriptions.Item>
                        <Descriptions.Item label="航路">
                            {flight.flight_route_id ? `${getFlightRouteName(flight) || `Route #${flight.flight_route_id}`} (${formatBlockTime(getFlightRouteBlockTime(flight))})` : <Text type="warning">未绑定航路</Text>}
                        </Descriptions.Item>
                        <Descriptions.Item label={timeInfo.departureLabel}>{renderTimeValue(timeInfo.departureTime, timeInfo.departureSourceLabel, usesEffectiveTime, flight.departure_airport)}</Descriptions.Item>
                        <Descriptions.Item label={timeInfo.arrivalLabel}>{renderTimeValue(timeInfo.arrivalTime, timeInfo.arrivalSourceLabel, usesEffectiveTime, flight.arrival_airport, timeInfo.departureTime)}</Descriptions.Item>
                        {usesEffectiveTime && timeInfo.departureSourceLabel === "ETD" ? <Descriptions.Item label="原 STD">{renderTimeValue(flight.std, "STD", false, flight.departure_airport)}</Descriptions.Item> : null}
                        {usesEffectiveTime && timeInfo.arrivalSourceLabel === "ETA" ? <Descriptions.Item label="原 STA">{renderTimeValue(flight.sta, "STA", false, flight.arrival_airport, flight.std)}</Descriptions.Item> : null}
                        {usesEffectiveTime ? <Descriptions.Item label="时间来源">执行参考轨按 ETD/ETA 优先定位；ATD/ATA 不参与编排。</Descriptions.Item> : null}
                        <Descriptions.Item label="锁定状态">{flight.is_fixed ? "已锁定" : "可编辑"}</Descriptions.Item>
                    </Descriptions>

                    <Form form={form} layout="vertical" disabled={!isEditableTrack || !canUpdateFlight || isDeletedFlight}>
                        <ScheduleDraftFlightForm editable={isEditableTrack && canUpdateFlight && !isDeletedFlight} showReadonlyHint={!isEditableTrack} onReferenceDataChange={handleReferenceDataChange} />
                    </Form>

                    <Space wrap size={[8, 8]}>
                        <Button type="primary" onClick={handleSubmit} loading={submitting} disabled={!isEditableTrack || !canUpdateFlight || isDeletedFlight}>
                            应用修改
                        </Button>
                        <Button danger onClick={handleDelete} loading={deletingFlight} disabled={!isEditableTrack || !canDeleteFlight || isDeletedFlight}>
                            删除航班
                        </Button>
                    </Space>
                </Space>
            ) : ground ? (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    <Space wrap size={[6, 6]}>
                        <Tag color="gold">机场</Tag>
                        <Tag>{ground.airportCode || ground.displayLabel || "不连续"}</Tag>
                        <Tag>{ground.isContinuous ? "连续过站" : "机场不衔接"}</Tag>
                    </Space>

                    <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label="机场">{ground.displayLabel || ground.airportCode || "--"}</Descriptions.Item>
                        <Descriptions.Item label="飞机">{ground.aircraftReg || "未分配"}</Descriptions.Item>
                        <Descriptions.Item label="地面时间">{ground.groundTimeLabel || "--"}</Descriptions.Item>
                        <Descriptions.Item label="前序到达">{ground.arrivalAirport || "--"}</Descriptions.Item>
                        <Descriptions.Item label="后继出发">{ground.departureAirport || "--"}</Descriptions.Item>
                        <Descriptions.Item label="宵禁展示">选中机场的适用宵禁窗口已在时间线当前轨道以背景区间显示。</Descriptions.Item>
                    </Descriptions>
                </Space>
            ) : (
                <Empty description="选中时间轴条目后可查看上下文" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
        </Card>
    );
};

export default ScheduleDraftFlightDetailPanel;
